import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { ApplicationInsightsService } from '@skylab-angular-shared/config';
import { injectTranslation, RoutingService } from '@skylab-angular-shared/core';
import { CompanyContextLink, TranslatePipe } from '@skylab-angular-shared/pipes';
import { ButtonComponent, DrawerDirective, IconComponent, injectToast, TooltipDirective } from '@skylab-angular-shared/ui';
import { AnnouncementContentKind } from '@skylab-shared/dataModel';
import { InfoAnnouncementToastComponent } from '../info-announcement-toast/info-announcement-toast.component';
import { AnnouncementItem, AnnouncementsService } from './announcements.service';

@Component({
	selector: 'skl-announcements-drawer',
	template: `
		<ng-template
			sklDrawer
			drawerWidth="65ch"
			#announcementsDrawer="sklDrawer"
		>

			<header class="drawer-header">
				<skl-button
					class="close-drawer-button"
					data-kind="icon-only"
					typeName="transparent"
					(click)="close()"
				>
					<skl-icon size="20px" name="arrow-r-right" />
				</skl-button>
				<h2>{{ 'scenes.announcements.label' | translate }}</h2>
				<div class="spacer"></div>

				@if (announcementsCollection().length > 0) {
					<skl-button
						class="close-all-button"
						data-kind="icon-only"
						typeName="transparent"
						(click)="closeAllAsync()"
						[tooltip]="'scenes.announcements.clear-all' | translate"
					>
						<skl-icon name="dismiss-square-multiple-regular" size="20px" />
					</skl-button>
				}
			</header>

			<section class="drawer-content">
				@for (item of announcementsCollection(); track item.id) {
					<div class="announcement-item" [attr.data-color]="item.color" [attr.data-testid]="item.contentType">
						@if (item.icon) {
							<div class="announcement-icon-frame">
								<skl-icon [name]="item.icon" />
							</div>
						}

						<div class="announcement-content">
							<header>
								<div class="announcement-title-group">
									<h2>{{ item.title }}</h2>
									@if (item.createdDate) {
										<time class="announcement-date">
											{{ item.createdDate | date: 'dd.MM.yyyy' }}
										</time>
									}
								</div>
								@if (item.canClose !== false) {
									<skl-button
										typeName="transparent"
										data-kind="icon-only"
										(click)="closeAnnouncementAsync(item)"
										[tooltip]="'scenes.announcements.close' | translate"
									>
										<skl-icon name="cross" />
									</skl-button>
								}
							</header>
							<article>
								@let data = parseContent(item.content);
								@if (item.templateContext) {
									@switch (item.contentType) {
										@case ('DocumentEmailDeliveryFailure') {
											<ng-container *ngTemplateOutlet="emailDelivery; context: { email: data.Recipient, number: data.DocumentNumber, route: item.templateContext.route }" />
										}
										@case ('UnsettledDocumentReminderDeliveryFailure') {
											<ng-container *ngTemplateOutlet="unsettledDocumentEmailDelivery; context: { email: data.Recipient, number: data.DocumentNumber, route: item.templateContext.route }" />
										}
										@case ('DocumentTemplatesDeactivated') {
											<ng-container *ngTemplateOutlet="documentTemplateDeactivated; context: { route: item.templateContext.route }" />
										}
										@case ('DocumentTemplateIssuingFailure') {
											<ng-container *ngTemplateOutlet="documentTemplateIssuingFailure; context: { route: item.templateContext.route, name: data.TemplateName }" />
										}
										@case ('KsefProcessingEnded') {
											<ng-container *ngTemplateOutlet="documentKsefProcessingEnded; context: { text: item.text, route: item.templateContext.route, number: data.DocumentNumber }" />
										}
										@case ('KsefReceivingEnded') {
											<ng-container *ngTemplateOutlet="documentKsefReceivingEnded; context: { text: item.text, route: item.templateContext.route }" />
										}
										@case ('NewOnlineReceiverAvailable') {
											<ng-container *ngTemplateOutlet="newOnlineReceiver; context: { text: item.text, route: item.templateContext.route }" />
										}
										@case ('BackgroundTaskStarted') {
											<ng-container *ngTemplateOutlet="backgroundTask; context: { text: item.text, route: item.templateContext?.route }" />
										}
										@case ('BackgroundTaskEnded') {
											<ng-container *ngTemplateOutlet="backgroundTask; context: { text: item.text, route: item.templateContext?.route }" />
										}
										@default {
											{{ item.text }}
										}
									}
								} @else if (item.template) {
									<span [innerHTML]="item.template"></span>
								} @else {
									{{ item.text }}
								}

								@if (item.outerUrl) {
									- <a class="link" (click)="handleHrefClick($event)" [href]="item.outerUrl" target="_blank">{{ item.outerUrlText }}</a>
								}
							</article>
						</div>
					</div>
				} @empty {
					<div class="no-data">
						<skl-icon name="notifications" size="48px" />
						<h3>{{ 'scenes.announcements.no-data' | translate }}</h3>
						<p>{{ 'scenes.announcements.no-data-description' | translate }}</p>
					</div>
				}
			</section>
		</ng-template>

		<ng-template #emailDelivery let-email="email" let-number="number" let-route="route">
			@if (!number) {
				{{ 'scenes.announcements.document-email-delivery-failure.email-delivery-failure-without-number' | translate: {email} }}
			} @else {
				{{'scenes.announcements.document-email-delivery-failure.email-delivery-failure' | translate}}
				<a (click)="handleHrefClick($event)" class="link" [routerLink]="route?.url | sklCompanyLink" [queryParams]="route?.params">{{ number }}</a>
				{{'scenes.announcements.document-email-delivery-failure.to-the-email-address' | translate: {email} }}
			}
		</ng-template>

		<ng-template #unsettledDocumentEmailDelivery let-email="email" let-number="number" let-route="route">
			@if (!number) {
				{{ 'scenes.announcements.unsettled-document-reminder-delivery-failure.email-delivery-failure-without-number' | translate: {email} }}
			} @else {
				{{'scenes.announcements.unsettled-document-reminder-email-delivery-failure.email-delivery-failure' | translate}}
				<a (click)="handleHrefClick($event)" class="link" [routerLink]="route?.url | sklCompanyLink" [queryParams]="route?.params">{{ number }}</a>
				{{'scenes.announcements.unsettled-document-reminder-email-delivery-failure.to-the-email-address' | translate: {email} }}
			}
		</ng-template>

		<ng-template #documentTemplateDeactivated let-route="route">
			{{ 'scenes.announcements.document-templates-deactivated' | translate }}
			<a class="link" (click)="handleHrefClick($event)" [routerLink]="route?.url | sklCompanyLink" [queryParams]="route?.params">{{ 'scenes.announcements.document-templates-deactivated-link' | translate }}</a>
		</ng-template>

		<ng-template #documentTemplateIssuingFailure let-route="route" let-name="name">
			{{ 'scenes.announcements.document-template-issuing-failure.content' | translate }}
			<a class="link" (click)="handleHrefClick($event)" [routerLink]="route?.url | sklCompanyLink" [queryParams]="route?.params">{{name}}</a>
		</ng-template>

		<ng-template #documentKsefProcessingEnded let-text="text" let-route="route" let-number="number">
			{{text}} - <a class="link" (click)="handleHrefClick($event)" [routerLink]="route?.url | sklCompanyLink" [queryParams]="route?.params">{{number}}</a>
		</ng-template>

		<ng-template #documentKsefReceivingEnded let-text="text" let-route="route">
			{{text}} - <a class="link" (click)="handleHrefClick($event)" [routerLink]="route?.url | sklCompanyLink" [queryParams]="route?.params">{{'scenes.announcements.ksef-receiving-ended.success.link' | translate}}</a>
		</ng-template>

		<ng-template #newOnlineReceiver let-text="text" let-route="route">
			{{'scenes.announcements.online-exchange-newer-receiver' | translate}} - <a class="link" (click)="handleHrefClick($event)" [routerLink]="route?.url | sklCompanyLink" [queryParams]="route?.params">{{'scenes.announcements.online-exchange-newer-receiver-link' | translate}}</a>
		</ng-template>

		<ng-template #backgroundTask let-text="text" let-route="route">
			{{ text }}
			@if (route) {
				<span class="align--tiny-right alignt--tiny-left">-</span>
				<a class="link" (click)="handleHrefClick($event)" [routerLink]="route?.url | sklCompanyLink" [queryParams]="route?.params">{{'scenes.announcements.imports.show-summary' | translate}}</a>
			}
		</ng-template>

	`,
	styles: `
		:host {
			display: contents;
		}

		.drawer-header {
			display: flex;
			align-items: center;
			gap: var(--gutter-small);
			padding: var(--gutter-medium);
			border-bottom: 1px solid rgb(0 0 0 / 24%);

			h2 {
				margin: 0;
				font-size: 1rem;
				font-weight: 500;
			}

			.spacer {
				flex: 1;
			}
		}

		.close-drawer-button,
		.close-all-button {
			min-width: 2.5rem;
		}

		.drawer-content {
			display: flex;
			flex-direction: column;
			gap: var(--gutter-medium);
			padding: var(--gutter-medium);
			overflow-y: auto;
		}

		.announcement-item {
			--_line-color: color-mix(in srgb, currentColor 10%, transparent);

			display: flex;
			align-items: center;
			gap: 1rem;
			padding: var(--gutter-medium);
			border-radius: .75rem;
			border: 1px solid light-dark(var(--line-1-color), transparent);

			background-color: light-dark(var(--surface-3-color), var(--surface-4-color));

			&[data-color="info"] {
				--_status-color: var(--info-color);
			}
			&[data-color="success"] {
				--_status-color: var(--success-color);
			}
			&[data-color="warning"] {
				--_status-color: var(--warning-color);
			}
			&[data-color="error"] {
				--_status-color: var(--error-color);
			}

			&[data-color] {
				.announcement-icon-frame {
					--_line-color: light-dark(
						color-mix(in oklch, var(--_status-color) 8%, transparent),
						color-mix(in oklch, var(--_status-color) 15%, transparent)
					);
					color: light-dark(
						color-mix(in oklch, var(--_status-color), black 20%),
						color-mix(in oklch, var(--_status-color), white 10%)
					);
				}
			}

			.announcement-icon-frame {
				color: var(--text-minor-color);
				display: grid;
				place-items: center;
				aspect-ratio: 1;
				padding: .75em;
				background-color: var(--_line-color);
				border-radius: .5rem;
				flex-shrink: 0;

				skl-icon {
					--icon-size: 1.25rem;
				}
			}

			.announcement-content {
				display: flex;
				flex-direction: column;
				gap: .25rem;
				flex: 1;

				header {
					display: flex;
					align-items: center;
					justify-content: space-between;
					gap: var(--gutter-small);

					h2 {
						margin: 0;
						flex: 1;
						font-size: 1rem;
						font-weight: 600;
						color: var(--text-color);
					}

					.announcement-title-group {
						display: flex;
						flex-direction: column;
						gap: 2px;
						flex: 1;

						.announcement-date {
							font-size: 0.75rem;
							font-weight: 400;
							color: var(--text-minor-color);
							opacity: 0.8;
						}
					}

					skl-button {
						opacity: 0.6;
						aspect-ratio: 1;

						&:hover {
							opacity: 1;
						}
					}
				}

				article {
					font-size: 0.875rem;
					line-height: 1.4;
					color: var(--text-minor-color);
					text-wrap: pretty;
				}
			}
		}

		.no-data {
			--icon-color: var(--text-minor-color);

			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			gap: 1rem;
			text-align: center;
			padding-block: 5rem;
			color: var(--text-minor-color);

			h3 {
				margin: 0;
				font-size: 1.1rem;
				font-weight: 600;
			}

			p {
				margin: 0;
				font-size: 0.9rem;
				opacity: 0.7;
				max-width: 50ch;
			}
		}

		.link {
			color: var(--primary-color-light);
			text-decoration: underline;
			cursor: pointer;
			text-underline-offset: 4px;
		}
	`,
	imports: [
		CommonModule,
		DatePipe,
		TranslatePipe,
		CompanyContextLink,
		DrawerDirective,
		TooltipDirective,
		RouterLink,
		ButtonComponent,
		IconComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnnouncementsDrawerComponent {

	readonly #T = injectTranslation();
	readonly #insights = inject(ApplicationInsightsService);
	readonly #announcementsService = inject(AnnouncementsService);
	readonly #routingService = inject(RoutingService);
	readonly #toast = injectToast();

	protected readonly announcementsDrawer = viewChild.required('announcementsDrawer', { read: DrawerDirective });

	readonly #announcements = toSignal(this.#announcementsService.announcements$.asObservable(), { initialValue: [] });
	readonly announcementsCollection = computed(() => this.#announcements().toReversed());
	readonly #TOAST_DELAY = 5000;
	readonly #TOAST_STAGGER = 1000;

	readonly #toastedIds = new Set<number>();

	public constructor() {
		this.#insights.trackEvent('FE_NOTIFICATION_POPUP_OPENED');

		effect(() => {
			const items = this.announcementsCollection().filter(item => !item.isDisplayedAsToast && item.id !== null && !this.#toastedIds.has(item.id));
			if (items.length > 0) {
				this.#processAnnouncementsBatch(items);
			}
		});
	}

	public open() {
		this.#toast.closeAll();
		this.announcementsDrawer().open();
	}

	public close() {
		this.announcementsDrawer().close();
	}

	public async closeAnnouncementAsync(item: AnnouncementItem) {
		await this.#announcementsService.closeAnnouncementAsync(item);
	}

	public async closeAllAsync() {
		await this.#announcementsService.closeAllAnnouncementsAsync();
	}

	public async handleHrefClick($event: Event): Promise<boolean> {
		const elem = $event?.target as HTMLAnchorElement | undefined;
		const url = elem?.href;

		if (!url) return true;

		this.close();

		const appState = this.#routingService.getStateFromUrl(url);

		if (!appState) {
			window.open(url, elem?.target || '_blank');
			return false;
		}

		if (appState.state.url === this.#routingService.currentState.url) {
			await this.#routingService.setQueryParamsAsync(appState.params);
			this.#routingService.reloadCurrentState();
			return false;
		}

		return true;
	}

	#processAnnouncementsBatch(newItems: AnnouncementItem[]) {
		newItems.forEach(item => {
			if (item.id !== null && item.id !== undefined) {
				this.#toastedIds.add(item.id);
			}
		});

		const validItems = newItems.filter((item): item is AnnouncementItem & { id: number; contentType: AnnouncementContentKind } => 
			item.id !== null && item.id !== undefined && item.contentType !== undefined && !this.#toast.hasId(item.id)
		);

		const grouped = validItems.reduce((acc, item) =>
			acc.set(item.contentType, [...(acc.get(item.contentType) ?? []), item]), 
			new Map<AnnouncementContentKind, AnnouncementItem[]>()
		);

		let index = 0;
		grouped.forEach((items, kind) => {
			const duration = this.#TOAST_DELAY + (index * this.#TOAST_STAGGER);
			items.length > 1 ? this.#toastRenderAggregated(kind, items, duration) : this.#toastRender(items[0], duration);
			index++;
		});
	}

	#toastRenderAggregated(kind: AnnouncementContentKind, items: AnnouncementItem[], duration: number) {
		const firstItem = items[0];

		this.#toast.open({
			duration: duration,
			type: firstItem.kind.toLowerCase() as any,
			closeButton: true,
			data: {
				id: firstItem.id ?? undefined,
				kind: kind,
				icon: firstItem.icon,
				title: firstItem.title,
				content: this.#T('scenes.announcements.aggregated-toast',
					{
						count: items.length,
						type: this.#T(`scenes.announcements.types.${kind}`)
					}
				)
			}
		});
	}

	#toastRender(item: AnnouncementItem, duration: number) {
		const base = {
			duration,
			type: item.kind.toLowerCase() as any,
			closeButton: true,
		};

		const commonData = {
			id: item.id ?? undefined,
			kind: item.contentType,
			icon: item.icon,
			title: item.title,
		};

		if (item.template) {
			this.#toast.open({
				...base,
				data: {
					...commonData,
					content: InfoAnnouncementToastComponent,
					contentProps: {
						template: item.template,
					}
				},
			});
		} else {
			this.#toast.open({
				...base,
				data: {
					...commonData,
					content: item.text ?? '',
				},
			});
		}
	}

	protected parseContent(content: string | undefined): any {
		if (!content) return {};
		try {
			return JSON.parse(content);
		} catch {
			return {};
		}
	}
}
